import React from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Paper, 
  Grid, 
  Card, 
  CardContent, 
  Button,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip
} from '@mui/material';
import {
  Email,
  Chat,
  Book,
  VideoLibrary,
  ExpandMore,
  CheckCircle
} from '@mui/icons-material';

const Support = () => {
  const [expanded, setExpanded] = React.useState(false);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const supportOptions = [
    {
      icon: <Email />,
      title: 'Email Support',
      description: 'Get detailed help from our support team. We respond to all inquiries within 24 hours.',
      details: 'support@superchargeme.com',
      responseTime: '4-24 hours',
      action: 'Send Email',
      href: 'mailto:support@superchargeme.com'
    },
    {
      icon: <Chat />,
      title: 'Live Chat',
      description: 'Chat with our support team for immediate assistance with technical issues.',
      details: 'Mon-Fri, 9 AM - 6 PM EST',
      responseTime: 'Under 5 minutes',
      action: 'Start Chat',
      href: '#'
    },
    {
      icon: <Book />,
      title: 'Documentation',
      description: 'Comprehensive guides, API documentation, and tutorials.',
      details: 'Setup, API, Integrations, Troubleshooting',
      responseTime: 'Available 24/7',
      action: 'View Docs',
      href: '#'
    },
    {
      icon: <VideoLibrary />,
      title: 'Video Tutorials',
      description: 'Step-by-step video guides for common tasks and advanced features.',
      details: 'Setup, Dashboards, Data Sync, Reporting',
      responseTime: 'Available 24/7',
      action: 'Watch Videos',
      href: '#'
    }
  ];

  const faqItems = [
    {
      question: '🔗 How do I connect my Shopify store?',
      answer: `Connecting your Shopify store is simple:
      1. Go to the Connectors section in your SuperchargeMe dashboard
      2. Click "Add New Connector" and select Shopify
      3. Enter your store domain (e.g., yourstore.myshopify.com)
      4. Click "Connect" and authorize SuperchargeMe to access your store data
      5. Your data will start syncing automatically within minutes`
    },
    {
      question: '📊 What data does SuperchargeMe collect from my store?',
      answer: `SuperchargeMe collects the following data with your permission:
      • Products: Product details, pricing, inventory levels
      • Orders: Order information, customer data, transaction details
      • Customers: Customer profiles and purchase history
      • Analytics: Store performance metrics and traffic data
      • Inventory: Stock levels and product variants
      
      All data is processed securely and used only for analytics and reporting purposes.`
    },
    {
      question: '🔒 Is my data secure?',
      answer: `Yes, we take data security very seriously:
      • All data is encrypted in transit and at rest
      • We use industry-standard security measures
      • Regular security audits and monitoring
      • GDPR compliant data handling
      • No data is sold or shared with unauthorized parties`
    },
    {
      question: '⚡ How often does data sync?',
      answer: `Data synchronization frequency depends on your plan:
      • Real-time: Order and inventory updates (within 5 minutes)
      • Hourly: Customer and product updates
      • Daily: Analytics and reporting data
      
      You can also trigger manual syncs from your dashboard.`
    },
    {
      question: '🔧 What if my data sync fails?',
      answer: `If data sync encounters issues:
      1. Check your connector status in the dashboard
      2. Verify your store credentials are still valid
      3. Try manually triggering a sync
      4. Check our status page for any service issues
      5. Contact support if the problem persists
      
      Most sync issues are resolved automatically within 15 minutes.`
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #4F46E5 0%, var(--color-primary) 50%, var(--color-accent) 100%)',
        color: 'white',
        p: 4,
        borderRadius: 2,
        textAlign: 'center',
        mb: 4
      }}>
        <Typography variant="h3" component="h1" gutterBottom>
          SuperchargeMe Support
        </Typography>
        <Typography variant="h6">
          Get help with your data analytics and integration platform
        </Typography>
      </Box>

      {/* Status Bar */}
      <Alert 
        icon={<CheckCircle />} 
        severity="success" 
        sx={{ mb: 3, textAlign: 'center' }}
      >
        <strong>🟢 All Systems Operational</strong> - API, Dashboard, and Data Sync services are running normally
      </Alert>

      {/* Support Options Grid */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {supportOptions.map((option, index) => (
          <Grid item xs={12} md={6} key={index}>
            <Card sx={{ height: '100%', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-4px)' } }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ 
                    backgroundColor: 'primary.main', 
                    color: 'white', 
                    p: 1, 
                    borderRadius: '50%',
                    mr: 2
                  }}>
                    {option.icon}
                  </Box>
                  <Typography variant="h6" component="h3">
                    {option.title}
                  </Typography>
                </Box>
                <Typography variant="body1" paragraph>
                  {option.description}
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  <strong>Details:</strong> {option.details}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                  <Chip 
                    label={`Response: ${option.responseTime}`} 
                    size="small" 
                    color="primary" 
                    variant="outlined"
                  />
                  <Button 
                    variant="contained" 
                    href={option.href}
                    onClick={option.href === '#' ? () => alert('Feature coming soon!') : undefined}
                  >
                    {option.action}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Contact Methods */}
      <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
        <Typography variant="h4" component="h2" gutterBottom color="primary">
          Contact Information
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1, borderLeft: '4px solid', borderColor: 'primary.main' }}>
              <Typography variant="h6" gutterBottom>General Support</Typography>
              <Typography variant="body2"><strong>Email:</strong> support@superchargeme.com</Typography>
              <Typography variant="body2"><strong>Response:</strong> 4-24 hours</Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>Technical issues, setup help, general questions</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1, borderLeft: '4px solid', borderColor: 'primary.main' }}>
              <Typography variant="h6" gutterBottom>Technical Support</Typography>
              <Typography variant="body2"><strong>Email:</strong> tech@superchargeme.com</Typography>
              <Typography variant="body2"><strong>Response:</strong> 2-12 hours</Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>API issues, integration problems, data sync errors</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1, borderLeft: '4px solid', borderColor: 'primary.main' }}>
              <Typography variant="h6" gutterBottom>Privacy & Legal</Typography>
              <Typography variant="body2"><strong>Email:</strong> privacy@superchargeme.com</Typography>
              <Typography variant="body2"><strong>Response:</strong> 2-5 business days</Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>Data privacy, GDPR requests, legal questions</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1, borderLeft: '4px solid', borderColor: 'primary.main' }}>
              <Typography variant="h6" gutterBottom>Sales & Billing</Typography>
              <Typography variant="body2"><strong>Email:</strong> billing@superchargeme.com</Typography>
              <Typography variant="body2"><strong>Response:</strong> 1-2 business days</Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>Subscription changes, billing issues, upgrades</Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* FAQ Section */}
      <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
        <Typography variant="h4" component="h2" gutterBottom color="primary">
          Frequently Asked Questions
        </Typography>
        {faqItems.map((item, index) => (
          <Accordion 
            key={index}
            expanded={expanded === `panel${index}`}
            onChange={handleChange(`panel${index}`)}
            sx={{ mb: 1 }}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6" component="h3">
                {item.question}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                {item.answer}
              </Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Paper>

      {/* Call to Action */}
      <Box sx={{ 
        bgcolor: 'primary.main', 
        color: 'white', 
        p: 4, 
        borderRadius: 2, 
        textAlign: 'center' 
      }}>
        <Typography variant="h5" gutterBottom>
          Still Need Help?
        </Typography>
        <Typography variant="body1" paragraph>
          Can't find what you're looking for? Our support team is here to help!
        </Typography>
        <Button 
          variant="contained" 
          size="large"
          sx={{ bgcolor: 'white', color: 'primary.main', '&:hover': { bgcolor: 'grey.100' } }}
          href="mailto:support@superchargeme.com"
        >
          Contact Support
        </Button>
      </Box>
    </Container>
  );
};

export default Support;
